import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { PlusCircle } from "lucide-react";
import { AddCommentModal } from "@/components/candidates/AddCommentModal";
import { toast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";

interface JobCommentsProps {
  jobId: string | undefined;
}

interface JobComment {
  id: string;
  content: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export const JobComments = ({ jobId }: JobCommentsProps) => {
  const { t } = useTranslation();
  const [comments, setComments] = useState<JobComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddCommentOpen, setIsAddCommentOpen] = useState(false);

  const fetchComments = async () => {
    if (!jobId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('job_comments')
        .select('*')
        .eq('job_id', jobId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setComments(data || []);
    } catch (error) {
      console.error('Error fetching job comments:', error);
      toast({
        title: "Error",
        description: "Could not load comments",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [jobId]);

  const handleAddComment = async (content: string) => {
    if (!jobId || !content) return;

    try {
      // In a real app, get the user's name from auth context
      const userName = "Current User"; // Placeholder

      const { data, error } = await supabase
        .from('job_comments')
        .insert({
          job_id: jobId,
          content,
          created_by: userName,
        })
        .select();

      if (error) throw error;

      // Add a record to job_history
      await supabase
        .from('job_history')
        .insert({
          job_id: jobId,
          action: "COMMENT_ADDED",
          description: "A comment was added to this job",
          performed_by: userName,
        });

      toast({
        title: "Success",
        description: "Comment added successfully",
      });

      // Refresh comments
      fetchComments();
    } catch (error) {
      console.error('Error adding comment:', error);
      toast({
        title: "Error",
        description: "Could not add comment",
        variant: "destructive",
      });
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">{t("companies.comments.title", "Comments")}</h3>
        <Button 
          onClick={() => setIsAddCommentOpen(true)}
          className="flex items-center gap-1"
        >
          <PlusCircle className="h-4 w-4" />
          <span>{t("companies.comments.addComment", "Add Comment")}</span>
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center p-8">
          <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
        </div>
      ) : (
        <>
          {comments.length === 0 ? (
            <p className="text-gray-500 italic text-center py-8">{t("companies.comments.noComments", "No comments yet")}</p>
          ) : (
            <div className="space-y-4">
              {comments.map((comment) => (
                <div key={comment.id} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between mb-2">
                    <span className="font-medium">{comment.created_by}</span>
                    <span className="text-sm text-gray-500">
                      {new Date(comment.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: comment.content }} />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <AddCommentModal
        isOpen={isAddCommentOpen}
        onClose={() => setIsAddCommentOpen(false)}
        onSave={handleAddComment}
      />
    </div>
  );
};
